import {DifficultySummaries, QuestionDifficulty, completionScoreBarColor} from '@domain/pom'
import {formatCompletion} from '@hconnect/common/utils'
import {GaugeBar} from '@hconnect/uikit/src/lib2'
import {Divider, Grid, Stack, Typography} from '@mui/material'
import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'

import {POM_COMPLETION_WIDGET_NAMESPACE, DIFFICULTY_LABEL_KEY} from '../consts'

import {RatingTooltip} from './RatingTooltip'

interface CompletionSectionProps {
  difficultySummaries: DifficultySummaries
  activeDifficulty: QuestionDifficulty
  onDifficultySectionClick?: (difficulty: QuestionDifficulty) => void
  'data-test-id'?: string
}

type BarSectionProps = {
  difficultySummaries: DifficultySummaries
  questionDifficulty: QuestionDifficulty
  activeDifficulty: QuestionDifficulty
}
const BarSection: FC<BarSectionProps> = ({
  activeDifficulty,
  questionDifficulty,
  difficultySummaries
}) => {
  const {t} = useTranslation(POM_COMPLETION_WIDGET_NAMESPACE)
  const isActiveDifficulty = activeDifficulty === questionDifficulty
  const color = isActiveDifficulty ? 'primary.light' : 'secondary.main'

  return (
    <Stack width="fit-content">
      <Typography variant="h5" color={color}>
        {t(DIFFICULTY_LABEL_KEY[questionDifficulty])}
      </Typography>
      <Typography variant="caption" fontWeight={600} color={color}>
        {difficultySummaries[questionDifficulty].answeredPositively}/
        {difficultySummaries[questionDifficulty].questionCount}
      </Typography>
      {isActiveDifficulty && <Divider sx={{mt: 0.5, borderWidth: 1, borderColor: color}} />}
    </Stack>
  )
}

export const CompletionSection: React.FC<CompletionSectionProps> = ({
  difficultySummaries,
  onDifficultySectionClick,
  activeDifficulty,
  'data-test-id': dataTestId
}) => {
  return (
    <Grid container spacing={0.25} data-test-id={dataTestId}>
      <Grid item xs={5} sm={6}>
        <RatingTooltip
          value={difficultySummaries.basic.positivePercent}
          summaries={difficultySummaries}
        >
          <GaugeBar
            value={difficultySummaries.basic.answeredPositively}
            maxValue={difficultySummaries.basic.questionCount}
            color={completionScoreBarColor(difficultySummaries, QuestionDifficulty.Basic)}
            minLabelWidth={56}
            barHeight={40}
            onClick={
              onDifficultySectionClick && (() => onDifficultySectionClick(QuestionDifficulty.Basic))
            }
            customSubtitle={
              <BarSection
                activeDifficulty={activeDifficulty}
                difficultySummaries={difficultySummaries}
                questionDifficulty={QuestionDifficulty.Basic}
              />
            }
            data-test-id="rating-basic"
          />
        </RatingTooltip>
      </Grid>
      <Grid item xs={4}>
        <GaugeBar
          value={difficultySummaries.advanced.answeredPositively}
          maxValue={difficultySummaries.advanced.questionCount}
          color={completionScoreBarColor(difficultySummaries, QuestionDifficulty.Advanced)}
          format={formatCompletion}
          minLabelWidth={56}
          barHeight={40}
          onClick={
            onDifficultySectionClick &&
            (() => onDifficultySectionClick(QuestionDifficulty.Advanced))
          }
          customSubtitle={
            <BarSection
              activeDifficulty={activeDifficulty}
              difficultySummaries={difficultySummaries}
              questionDifficulty={QuestionDifficulty.Advanced}
            />
          }
          data-test-id="rating-advanced"
        />
      </Grid>
      <Grid item xs={3} sm={2}>
        <GaugeBar
          value={difficultySummaries.excellence.answeredPositively}
          maxValue={difficultySummaries.excellence.questionCount}
          color={completionScoreBarColor(difficultySummaries, QuestionDifficulty.Excellence)}
          format={formatCompletion}
          minLabelWidth={56}
          barHeight={40}
          onClick={
            onDifficultySectionClick &&
            (() => onDifficultySectionClick(QuestionDifficulty.Excellence))
          }
          customSubtitle={
            <BarSection
              activeDifficulty={activeDifficulty}
              difficultySummaries={difficultySummaries}
              questionDifficulty={QuestionDifficulty.Excellence}
            />
          }
          data-test-id="rating-excellence"
        />
      </Grid>
    </Grid>
  )
}
